/**
 * Availability — pure derivation.
 *
 * The week's availability rows (approved time off, training, restricted
 * duty, reassignment, recorded absence, and PENDING time-off / call-out
 * requests) indexed so every view can ask the same two questions about a
 * card: "is this person actually out for this shift?" and "is there a
 * request pending?". A row is matched on the card's shift first; only a card
 * with no shift (or no row on its shift) falls back to the day. Nothing here
 * touches React or the server.
 */
import { format, parseISO } from "date-fns";

import { AvailabilityKind, AvailabilityRow } from "../../../../../services/roster-assignments-api";

/** Every kind but `requested` is confirmed unavailability. */
export const CONFIRMED_KINDS: ReadonlySet<AvailabilityKind> = new Set<AvailabilityKind>([
	"timeOff",
	"training",
	"restrictedDuty",
	"reassigned",
	"absent",
	"unavailable",
]);

export const isConfirmedRow = (row: AvailabilityRow): boolean => CONFIRMED_KINDS.has(row.kind);
export const isRequestedRow = (row: AvailabilityRow): boolean => row.kind === "requested";

export interface AvailabilityIndex {
	rows: AvailabilityRow[];
	/** Rows about a worker on one shift instance. */
	forWorkerShift(workerId: string, shiftId: string | null | undefined): AvailabilityRow[];
	/** Rows about a worker on a calendar day (yyyy-MM-dd). */
	forWorkerDay(workerId: string, dayIso: string): AvailabilityRow[];
	/** Every row about a worker in the loaded range, in date order. */
	forWorker(workerId: string): AvailabilityRow[];
	/** Confirmed out on the shift (a shift id) or the day (yyyy-MM-dd). */
	isOut(workerId: string, shiftOrDay: string | null | undefined): boolean;
	/** A pending request on the shift (a shift id) or the day (yyyy-MM-dd). */
	isRequested(workerId: string, shiftOrDay: string | null | undefined): boolean;
	/**
	 * The row that decides a card's state: the card's shift when any row is on
	 * it, else its day; a confirmed row beats a pending request. Null when the
	 * person is simply expected.
	 */
	lookup(workerId: string, shiftId: string | null | undefined, dayIso: string): AvailabilityRow | null;
}

const NO_ROWS: AvailabilityRow[] = [];

const dayOf = (value: string): string => value.slice(0, 10);

/** Confirmed first, then pending; within a tier the earlier window first. */
const decide = (rows: AvailabilityRow[]): AvailabilityRow | null => {
	if (!rows.length) {
		return null;
	}
	const sorted = [...rows].sort(
		(a, b) => Number(isConfirmedRow(b)) - Number(isConfirmedRow(a)) || a.startTime.localeCompare(b.startTime)
	);
	return sorted[0];
};

export function indexAvailability(rows: AvailabilityRow[]): AvailabilityIndex {
	const byShift = new Map<string, AvailabilityRow[]>();
	const byDay = new Map<string, AvailabilityRow[]>();
	const byWorker = new Map<string, AvailabilityRow[]>();
	const push = (map: Map<string, AvailabilityRow[]>, key: string, row: AvailabilityRow): void => {
		map.set(key, [...(map.get(key) || []), row]);
	};
	rows.forEach((row) => {
		const worker = String(row.workerId);
		if (row.shiftId) {
			push(byShift, `${worker}::${String(row.shiftId)}`, row);
		}
		push(byDay, `${worker}::${dayOf(row.date)}`, row);
		push(byWorker, worker, row);
	});
	byWorker.forEach((list) => list.sort((a, b) => a.startTime.localeCompare(b.startTime)));

	const forWorkerShift = (workerId: string, shiftId: string | null | undefined): AvailabilityRow[] =>
		shiftId ? byShift.get(`${String(workerId)}::${String(shiftId)}`) ?? NO_ROWS : NO_ROWS;
	const forWorkerDay = (workerId: string, dayIso: string): AvailabilityRow[] =>
		byDay.get(`${String(workerId)}::${dayOf(dayIso)}`) ?? NO_ROWS;
	const forWorker = (workerId: string): AvailabilityRow[] => byWorker.get(String(workerId)) ?? NO_ROWS;
	// A shift id and a yyyy-MM-dd live in different key spaces, so one lookup
	// serves both: whichever map knows the key answers.
	const rowsFor = (workerId: string, shiftOrDay: string | null | undefined): AvailabilityRow[] => {
		if (!shiftOrDay) {
			return NO_ROWS;
		}
		const onShift = forWorkerShift(workerId, shiftOrDay);
		return onShift.length ? onShift : forWorkerDay(workerId, shiftOrDay);
	};

	return {
		rows,
		forWorkerShift,
		forWorkerDay,
		forWorker,
		isOut: (workerId, shiftOrDay) => rowsFor(workerId, shiftOrDay).some(isConfirmedRow),
		isRequested: (workerId, shiftOrDay) => rowsFor(workerId, shiftOrDay).some(isRequestedRow),
		lookup: (workerId, shiftId, dayIso) => {
			const onShift = forWorkerShift(workerId, shiftId);
			return decide(onShift.length ? onShift : forWorkerDay(workerId, dayIso));
		},
	};
}

/** The index of nothing — every lookup says "expected". Shared so memos stay stable. */
export const EMPTY_AVAILABILITY: AvailabilityIndex = indexAvailability([]);

// ---------------------------------------------------------------------------

export type AvailabilityTone = "danger" | "warning";

export interface AvailabilityBadge {
	/** What the chip prints: "OUT" for confirmed unavailability, "TIME OFF?" (or "CALL-OUT?") for a pending request. */
	text: string;
	tone: AvailabilityTone;
	/** "Time Off · approved · Tue 6 Oct 14:30–23:00" / "Leave Request · pending · dental appointment". */
	tooltip: string;
}

const safeParse = (iso: string): Date | null => {
	const date = parseISO(iso);
	return Number.isNaN(date.getTime()) ? null : date;
};

/** "Tue 6 Oct 14:30–23:00", or just the day when the times do not parse. */
export const availabilityWindow = (row: AvailabilityRow): string => {
	const start = safeParse(row.startTime);
	const end = safeParse(row.endTime);
	const day = safeParse(row.date) ?? start;
	const dayText = day ? format(day, "EEE d MMM") : row.date;
	if (!start || !end) {
		return dayText;
	}
	return `${dayText} ${format(start, "HH:mm")}–${format(end, "HH:mm")}`;
};

const requestedText = (row: AvailabilityRow): string => {
	const label = row.label.toLowerCase();
	if (label.includes("call")) {
		return "CALL-OUT?";
	}
	return "TIME OFF?";
};

export function availabilityBadge(row: AvailabilityRow): AvailabilityBadge {
	const confirmed = isConfirmedRow(row);
	const status = row.requestStatus ? row.requestStatus.toLowerCase() : confirmed ? "confirmed" : "pending";
	const detail = row.detail?.trim();
	return {
		text: confirmed ? "OUT" : requestedText(row),
		tone: confirmed ? "danger" : "warning",
		tooltip: [row.label, status, detail || availabilityWindow(row)].join(" · "),
	};
}

/** "out Tue, Wed" — the days a worker is confirmed out, for a list entry. */
export function outDaysLabel(rows: AvailabilityRow[]): string | null {
	const days = Array.from(new Set(rows.filter(isConfirmedRow).map((row) => dayOf(row.date)))).sort();
	if (!days.length) {
		return null;
	}
	return `out ${days
		.map((day) => {
			const date = safeParse(day);
			return date ? format(date, "EEE") : day;
		})
		.join(", ")}`;
}
